import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '~/lib/api';

const STEPS = ['Conectar Gmail', 'Conectar NetSuite', 'Sincronizar'];

type AuthMethod = 'tba' | 'oauth2';

export default function TbitInvoicesConnectPage() {
  const [searchParams] = useSearchParams();
  const initialStep = Number(searchParams.get('step')) || 0;
  const [step, setStep] = useState(initialStep);
  const [error, setError] = useState('');
  const [gmailLoading, setGmailLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Auth method
  const [authMethod, setAuthMethod] = useState<AuthMethod>('tba');

  // NetSuite shared state
  const [nsAccountId, setNsAccountId] = useState('');
  const [nsSubsidiaryId, setNsSubsidiaryId] = useState('');
  const [nsSaving, setNsSaving] = useState(false);

  // OAuth 2.0 fields
  const [nsClientId, setNsClientId] = useState('');
  const [nsClientSecret, setNsClientSecret] = useState('');

  // TBA fields
  const [nsConsumerKey, setNsConsumerKey] = useState('');
  const [nsConsumerSecret, setNsConsumerSecret] = useState('');
  const [nsTokenId, setNsTokenId] = useState('');
  const [nsTokenSecret, setNsTokenSecret] = useState('');

  // Inbox form state
  const [inboxEmail, setInboxEmail] = useState('');
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const [inboxSaving, setInboxSaving] = useState(false);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ accounts: number; vendors: number } | null>(null);

  // Check setup status on mount to auto-skip completed steps
  useEffect(() => {
    apiFetch('/tbit-invoices/api/session')
      .then(res => res.json())
      .then(data => {
        if (data.setup_complete) {
          navigate('/tbit-invoices/dashboard', { replace: true });
          return;
        }
        // Auto-advance past completed steps (only if no explicit step param)
        if (!searchParams.get('step')) {
          if (data.has_gmail && data.has_netsuite) setStep(2);
          else if (data.has_gmail) setStep(1);
        }
      })
      .catch(() => { /* proceed with wizard */ })
      .finally(() => setLoading(false));
  }, []);

  const handleConnectGmail = async () => {
    setGmailLoading(true);
    setError('');
    try {
      const res = await apiFetch('/tbit-invoices/auth/google-url');
      if (!res.ok) throw new Error('No se pudo obtener la URL de autorización');
      const data = await res.json();
      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message);
      setGmailLoading(false);
    }
  };

  const handleNetSuiteConnect = async () => {
    setError('');
    setNsSaving(true);
    try {
      const payload: Record<string, string> = {
        auth_method: authMethod,
        account_id: nsAccountId.trim(),
        subsidiary_id: nsSubsidiaryId.trim(),
      };

      if (authMethod === 'oauth2') {
        payload.client_id = nsClientId.trim();
        payload.client_secret = nsClientSecret.trim();
      } else {
        payload.consumer_key = nsConsumerKey.trim();
        payload.consumer_secret = nsConsumerSecret.trim();
        payload.token_id = nsTokenId.trim();
        payload.token_secret = nsTokenSecret.trim();
      }

      const res = await apiFetch('/tbit-invoices/api/netsuite/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Error al conectar con NetSuite');
      }
      setStep(2);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setNsSaving(false);
    }
  };

  const handleSyncAndInbox = async () => {
    setError('');

    // Save inbox config first
    setInboxSaving(true);
    try {
      if (inboxEmail.trim()) {
        const inboxRes = await apiFetch('/tbit-invoices/api/config/inbox', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            monitored_email: inboxEmail.trim(),
            active: pollingEnabled,
          }),
        });
        if (!inboxRes.ok) {
          const data = await inboxRes.json().catch(() => null);
          throw new Error(data?.error || 'Error al configurar la bandeja');
        }
      }
    } catch (err: any) {
      setError(err.message);
      setInboxSaving(false);
      return;
    }
    setInboxSaving(false);

    // Then sync NetSuite data
    setSyncing(true);
    try {
      const res = await apiFetch('/tbit-invoices/api/netsuite/sync', {
        method: 'POST',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Error al sincronizar con NetSuite');
      }
      const data = await res.json();
      setSyncResult({ accounts: data.accounts_count ?? 0, vendors: data.vendors_count ?? 0 });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleFinish = () => {
    navigate('/tbit-invoices/dashboard');
  };

  const isNetSuiteFormValid = () => {
    if (!nsAccountId.trim()) return false;
    if (authMethod === 'oauth2') {
      return !!(nsClientId.trim() && nsClientSecret.trim());
    }
    return !!(nsConsumerKey.trim() && nsConsumerSecret.trim() && nsTokenId.trim() && nsTokenSecret.trim());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center px-4">
      <div className="card bg-base-100 shadow-lg w-full max-w-lg">
        <div className="card-body">
          <h1 className="text-2xl font-bold text-center mb-2">Configurar TBit Invoices</h1>

          {/* Stepper */}
          <ul className="steps steps-horizontal w-full mb-6 text-xs">
            {STEPS.map((label, i) => (
              <li key={label} className={`step ${i <= step ? 'step-primary' : ''}`}>
                {label}
              </li>
            ))}
          </ul>

          {error && (
            <div className="alert alert-error text-sm mb-4">
              <span>{error}</span>
            </div>
          )}

          {/* Step 0: Connect Gmail (permission, not identity) */}
          {step === 0 && (
            <div className="space-y-4 text-center">
              <p className="text-sm text-base-content/70">
                Conecta tu bandeja de Gmail para monitorear facturas entrantes.
              </p>
              <button onClick={handleConnectGmail} disabled={gmailLoading} className="btn btn-primary btn-wide">
                {gmailLoading ? 'Redirigiendo...' : 'Conectar Gmail'}
              </button>
            </div>
          )}

          {/* Step 1: NetSuite Credentials */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-base-content/70">
                Ingresa tus credenciales de API de NetSuite para sincronizar facturas.
              </p>

              {/* Auth method selector */}
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`btn btn-sm flex-1 ${authMethod === 'tba' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setAuthMethod('tba')}
                >
                  Token-Based Auth (TBA)
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-1 ${authMethod === 'oauth2' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setAuthMethod('oauth2')}
                >
                  OAuth 2.0
                </button>
              </div>

              <div className="form-control">
                <label className="label"><span className="label-text">Account ID</span></label>
                <input
                  type="text"
                  value={nsAccountId}
                  onChange={e => setNsAccountId(e.target.value)}
                  placeholder="ej. 1234567"
                  className="input input-bordered w-full"
                />
              </div>

              {/* OAuth 2.0 fields */}
              {authMethod === 'oauth2' && (
                <>
                  <div className="form-control">
                    <label className="label"><span className="label-text">Client ID</span></label>
                    <input
                      type="text"
                      value={nsClientId}
                      onChange={e => setNsClientId(e.target.value)}
                      className="input input-bordered w-full"
                    />
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text">Client Secret</span></label>
                    <input
                      type="password"
                      value={nsClientSecret}
                      onChange={e => setNsClientSecret(e.target.value)}
                      className="input input-bordered w-full"
                    />
                  </div>
                </>
              )}

              {/* TBA fields */}
              {authMethod === 'tba' && (
                <>
                  <div className="form-control">
                    <label className="label"><span className="label-text">Consumer Key</span></label>
                    <input
                      type="text"
                      value={nsConsumerKey}
                      onChange={e => setNsConsumerKey(e.target.value)}
                      className="input input-bordered w-full"
                    />
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text">Consumer Secret</span></label>
                    <input
                      type="password"
                      value={nsConsumerSecret}
                      onChange={e => setNsConsumerSecret(e.target.value)}
                      className="input input-bordered w-full"
                    />
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text">Token ID</span></label>
                    <input
                      type="text"
                      value={nsTokenId}
                      onChange={e => setNsTokenId(e.target.value)}
                      className="input input-bordered w-full"
                    />
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text">Token Secret</span></label>
                    <input
                      type="password"
                      value={nsTokenSecret}
                      onChange={e => setNsTokenSecret(e.target.value)}
                      className="input input-bordered w-full"
                    />
                  </div>
                </>
              )}

              <div className="form-control">
                <label className="label"><span className="label-text">Subsidiary ID</span></label>
                <input
                  type="text"
                  value={nsSubsidiaryId}
                  onChange={e => setNsSubsidiaryId(e.target.value)}
                  placeholder="ej. 1"
                  className="input input-bordered w-full"
                />
              </div>
              <button
                onClick={handleNetSuiteConnect}
                disabled={nsSaving || !isNetSuiteFormValid()}
                className="btn btn-primary w-full"
              >
                {nsSaving ? 'Conectando...' : 'Conectar NetSuite'}
              </button>
            </div>
          )}

          {/* Step 2: Inbox Config + NetSuite Sync (combined) */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-base-content/70">
                Configura tu bandeja de entrada y sincroniza cuentas desde NetSuite.
              </p>

              <div className="form-control">
                <label className="label"><span className="label-text">Email a monitorear</span></label>
                <input
                  type="email"
                  value={inboxEmail}
                  onChange={e => setInboxEmail(e.target.value)}
                  placeholder="facturas@tuempresa.com"
                  className="input input-bordered w-full"
                />
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Activar monitoreo automatico</span>
                  <input
                    type="checkbox"
                    checked={pollingEnabled}
                    onChange={e => setPollingEnabled(e.target.checked)}
                    className="toggle toggle-primary"
                  />
                </label>
              </div>

              <button
                onClick={handleSyncAndInbox}
                disabled={syncing || inboxSaving}
                className="btn btn-primary w-full"
              >
                {inboxSaving ? 'Guardando...' : syncing ? 'Sincronizando...' : 'Guardar y sincronizar'}
              </button>

              {syncResult && (
                <div className="alert alert-success text-sm">
                  <span>
                    Se sincronizaron {syncResult.accounts} cuenta{syncResult.accounts !== 1 ? 's' : ''} y {syncResult.vendors} proveedor{syncResult.vendors !== 1 ? 'es' : ''} desde NetSuite.
                  </span>
                </div>
              )}

              {syncResult && (
                <button
                  onClick={handleFinish}
                  className="btn btn-outline btn-primary w-full"
                >
                  Ir al Panel
                </button>
              )}
            </div>
          )}

          {/* Skip link */}
          {step < 2 && (
            <div className="text-center mt-4">
              <button
                onClick={() => setStep(s => s + 1)}
                className="btn btn-ghost btn-sm text-base-content/50"
              >
                Omitir este paso
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
